import { useState, useEffect } from 'react';
import { useSignalRListener } from '../../hooks/useSignalR';
import { useSignalR } from '../../providers/SignalR';
import axios from 'axios';
import styled from 'styled-components';

const Container = styled.div`
  max-width: 800px;
  margin: 40px auto;
  padding: 20px;
  font-family:
    -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
`;

const Title = styled.h1`
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 28px;
`;

const Subtitle = styled.p`
  color: #7f8c8d;
  margin-bottom: 30px;
  font-size: 14px;
`;

const Section = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.h2`
  color: #34495e;
  margin-bottom: 20px;
  font-size: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Badge = styled.span`
  background: ${props => props.color || '#3498db'};
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  margin-bottom: 12px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #3498db;
    box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
  }
`;

const Button = styled.button`
  background: ${props => (props.variant === 'secondary' ? '#95a5a6' : '#3498db')};
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  margin-right: 10px;
  transition: all 0.2s;

  &:hover {
    background: ${props => (props.variant === 'secondary' ? '#7f8c8d' : '#2980b9')};
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: #bdc3c7;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const MessageList = styled.div`
  max-height: 400px;
  overflow-y: auto;
  margin-top: 20px;
`;

const MessageItem = styled.div`
  background: #f8f9fa;
  border-left: 4px solid #3498db;
  padding: 16px;
  margin-bottom: 12px;
  border-radius: 4px;
  animation: slideIn 0.3s ease-out;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateX(-20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;

const MessageContent = styled.div`
  color: #2c3e50;
  font-size: 16px;
  margin-bottom: 8px;
  font-weight: 500;
`;

const MessageMeta = styled.div`
  color: #95a5a6;
  font-size: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ConnectionStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: ${props => (props.connected ? '#d4edda' : '#f8d7da')};
  color: ${props => (props.connected ? '#155724' : '#721c24')};
  border-radius: 6px;
  margin-bottom: 20px;
  font-size: 14px;
`;

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => (props.connected ? '#28a745' : '#dc3545')};
  animation: ${props => (props.connected ? 'pulse 2s infinite' : 'none')};

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

const InfoBox = styled.div`
  background: #e3f2fd;
  border-left: 4px solid #2196f3;
  padding: 16px;
  margin-bottom: 20px;
  border-radius: 4px;
  font-size: 14px;
  color: #1565c0;
`;

const CodeBlock = styled.pre`
  background: #263238;
  color: #aed581;
  padding: 16px;
  border-radius: 6px;
  overflow-x: auto;
  font-size: 13px;
  margin-top: 12px;
`;

const SignalRDemo = () => {
  const [messages, setMessages] = useState([]);
  const [customMessage, setCustomMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const { isConnected, connectionId, service } = useSignalR();

  useEffect(() => {
    console.log('SignalR Connection Status:', { isConnected, connectionId });
  }, [isConnected, connectionId]);

  // Listen for demo notifications
  useSignalRListener('DemoNotification', data => {
    console.log('✅ Received DemoNotification:', data);
    setMessages(prev => [
      {
        ...data,
        receivedAt: new Date().toLocaleTimeString()
      },
      ...prev
    ]);
  });

  const sendMessage = async (message = customMessage) => {
    setLoading(true);
    try {
      const apiUrl = import.meta.env.VITE_BASE_URL || '';
      const response = await axios.post(`${apiUrl}/demo/signalr/send`, {
        message: message || customMessage,
        target: 'DemoNotification'
      });
      console.log('Message sent:', response.data);
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const sendPing = async () => {
    setLoading(true);
    try {
      const apiUrl = import.meta.env.VITE_BASE_URL || '';
      const response = await axios.get(`${apiUrl}/demo/signalr/ping`);
      console.log('Ping sent:', response.data);
    } catch (error) {
      console.error('Error sending ping:', error);
      alert('Failed to send ping: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const clearMessages = () => {
    setMessages([]);
  };

  return (
    <Container>
      <Title>SignalR Demo</Title>
      <Subtitle>Real-time communication between backend and frontend</Subtitle>

      <ConnectionStatus connected={isConnected}>
        <StatusDot connected={isConnected} />
        {isConnected ? (
          <div>
            <strong>Connected to SignalR</strong>
            {connectionId && (
              <div style={{ fontSize: '12px', marginTop: '4px' }}>
                Connection ID: {connectionId}
              </div>
            )}
          </div>
        ) : (
          'Waiting for connection... (Please ensure you are logged in)'
        )}
      </ConnectionStatus>

      <InfoBox>
        <strong>How it works:</strong>
        <br />
        1. This page listens for <code>DemoNotification</code> messages from the SignalR hub
        <br />
        2. Click the buttons below to trigger the backend to send messages
        <br />
        3. Messages will appear in real-time below
        <br />
        <br />
        <strong>Note:</strong> You must be logged in for SignalR connection to work.
      </InfoBox>

      <Section>
        <SectionTitle>
          <span>📤 Send Messages</span>
          <Badge color="#3498db">Backend → Frontend</Badge>
        </SectionTitle>

        <Input
          type="text"
          placeholder="Enter custom message..."
          value={customMessage}
          onChange={e => setCustomMessage(e.target.value)}
          onKeyPress={e => e.key === 'Enter' && sendMessage()}
        />

        <div>
          <Button onClick={() => sendMessage()} disabled={loading || !customMessage}>
            {loading ? 'Sending...' : 'Send Custom Message'}
          </Button>
          <Button onClick={sendPing} disabled={loading} variant="secondary">
            {loading ? 'Sending...' : 'Send Ping'}
          </Button>
        </div>
      </Section>

      <Section>
        <SectionTitle>
          <span>📥 Received Messages</span>
          <Badge color="#27ae60">{messages.length} received</Badge>
        </SectionTitle>

        {messages.length > 0 && (
          <Button onClick={clearMessages} variant="secondary">
            Clear Messages
          </Button>
        )}

        <MessageList>
          {messages.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#95a5a6', padding: '40px 0' }}>
              No messages received yet. Send a message to see it appear here!
            </div>
          ) : (
            messages.map((msg, index) => (
              <MessageItem key={index}>
                <MessageContent>{msg.message}</MessageContent>
                <MessageMeta>
                  <span>From: {msg.sender || 'unknown'}</span>
                  <span>Received: {msg.receivedAt}</span>
                  <span>Sent: {new Date(msg.timestamp).toLocaleTimeString()}</span>
                </MessageMeta>
              </MessageItem>
            ))
          )}
        </MessageList>
      </Section>

      <Section>
        <SectionTitle>🔧 Implementation Details</SectionTitle>

        <div style={{ marginBottom: '16px' }}>
          <strong>Frontend (React):</strong>
          <CodeBlock>{`import { useSignalRListener } from '../../hooks/useSignalR';

// Listen for messages
useSignalRListener('DemoNotification', (data) => {
  console.log('Received:', data);
  setMessages(prev => [data, ...prev]);
});`}</CodeBlock>
        </div>

        <div>
          <strong>Backend (Python/FastAPI):</strong>
          <CodeBlock>{`@router.post("/demo/signalr/send")
async def send_demo_message(demo: SignalRDemoMessage):
    signalr_service = get_signalr_service(connection_string)
    
    message_data = {
        "message": demo.message,
        "timestamp": datetime.utcnow().isoformat(),
        "sender": "backend-demo"
    }
    
    await signalr_service.send_to_all(
        method="DemoNotification",
        data=message_data
    )`}</CodeBlock>
        </div>
      </Section>
    </Container>
  );
};

export default SignalRDemo;
